import { skipToken } from "@tanstack/query-core";
import { UseQueryOptions } from "@tanstack/react-query";

import { APIParams, MutationFunction, Prefixes } from "./types";

// asQuery can be used for easier compatibility between the simplydo codebase and the react-query library, for the most part this just transforms the callback pattern to a promise. Under the hood we also make some typescript inference to make sure useQuery can infer the correct parameters and return type of the queryFn
// Generally just wrapping any API function with asQuery will make it compatible with react-query's useQuery hook as the value for the queryFn
// This will only work for api functions that end with the typical success and fail callback pattern (which should be all of them!)
export const asQuery = <
  T extends (...args: any[]) => any,
  APIParam extends APIParams<T>,
  TBody extends Parameters<APIParam[1]>[0],
>(
  fn: T,
  params: APIParam[0] extends { 0: any } ? APIParam[0] : [],
  options?: { onSuccess?: () => void; onError?: () => void },
): (() => Promise<TBody>) => {
  return async () =>
    await new Promise((resolve, reject) => {
      fn(
        ...(params ?? []),
        (response: TBody) => {
          options?.onSuccess?.();
          resolve(response);
        },
        (error) => {
          options?.onError?.();
          reject(error);
        },
      );
    });
};

export const queryKey = <
  T extends (...args: any[]) => any,
  APIParam extends APIParams<T>,
>(
  fn: T & { queryRoute?: string; queryName?: string },
  params?: APIParam[0] | Prefixes<APIParam[0]>,
): string[] => {
  return [
    fn.queryRoute ?? "common",
    fn.queryName ?? fn.name,
    ...(params ?? []),
  ];
};

export const initUseQuery = <
  T extends (...args: any[]) => any,
  APIParam extends APIParams<T>,
  TBody extends Parameters<APIParam[1]>[0],
  E extends boolean,
>(
  fn: T,
  params: APIParam[0] extends { 0: any } ? APIParam[0] : [],
  options?: {
    onSuccess?: () => void;
    onError?: () => void;
    isEnabled?: E;
    placeholderData?: (data: TBody | undefined) => TBody | undefined;
  } & Omit<
    UseQueryOptions<TBody, Error, TBody, string[]>,
    "queryKey" | "queryFn" | "enabled"
  >,
): { queryKey: string[]; queryFn: () => Promise<TBody> } & Omit<
  UseQueryOptions<TBody, Error, TBody, string[]>,
  "queryKey" | "queryFn" | "enabled"
> => {
  const { onSuccess, onError, isEnabled, ...rest } = options ?? {};

  return {
    queryKey: queryKey(fn, params),
    queryFn: (isEnabled === false ? skipToken : (
      asQuery(fn, params, { onSuccess, onError })
    )) as () => Promise<TBody>,
    ...rest,
  };
};

export const querySetter = <
  T extends (...args: any[]) => any,
  APIParam extends APIParams<T>,
  TBody extends Parameters<APIParam[1]>[0],
  Undef extends boolean,
>(
  fn: T,
  dataOrCallback:
    | undefined
    | TBody
    | (Undef extends true ?
        (data: TBody | undefined, variables: APIParam[0]) => TBody | undefined
      : (data: TBody, variables: APIParam[0]) => TBody),
  options?: { allowUndefined?: Undef },
) => {
  if (typeof dataOrCallback !== "function") {
    return dataOrCallback;
  }

  return (data: TBody | undefined, variables: APIParam[0]): TBody => {
    if (!options?.allowUndefined && !data) {
      return data as TBody;
    }

    const updater = dataOrCallback as Undef extends true ?
      (data: TBody | undefined, variables: APIParam[0]) => TBody | undefined
    : (data: TBody, variables: APIParam[0]) => TBody;
    return updater(data, variables);
  };
};

// This is conceptually the same as asQuery, but for mutations instead of queries
// Instead of having to supply parameters directly to the definition, you supply them to the .mutate function call later.
export const asMutation = <
  T extends (...args: any[]) => any,
  APIParam extends APIParams<T>,
  TBody extends Parameters<APIParam[1]>[0],
  TParams extends APIParam[0] extends { 0: any } ? APIParam[0] : never,
>(
  fn: T,
): MutationFunction<TBody, TParams> => {
  return async (vars: TParams) =>
    await new Promise((resolve, reject) => {
      fn(
        ...(vars ?? []),
        (response: TBody) => {
          resolve(response);
        },
        (error) => {
          reject(error);
        },
      );
    });
};
